import React, { useState, useRef, useCallback } from 'react';
import {
    Box, Drawer, Typography, Button, IconButton, CircularProgress,
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    Send as SendIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    Science as ScienceIcon,
} from '@mui/icons-material';
import Type1GRRForm, { DEFAULT_FORM_VALUES } from '../components/Type1GRRForm';
import { apiFetch } from '../api/apiFetch';
import { apiErrorMessage } from '../api/apiError';
import Type1DataEntryTable from '../components/Type1DataEntryTable';
import Type1GRRResults from '../components/Type1GRRResults';

const DRAWER_WIDTH = 360;

function Type1GRRPage({ onBack }) {
    const tableRef = useRef(null);

    const [formValues, setFormValues]   = useState(DEFAULT_FORM_VALUES);
    const [drawerOpen, setDrawerOpen]   = useState(true);
    const [submitting, setSubmitting]   = useState(false);
    const [studyResults, setStudyResults] = useState(null);  // { id, createdAt, description, results }
    const [error, setError]             = useState(null);

    // ---------------------------------------------------------------------------
    // Submit
    // ---------------------------------------------------------------------------
    const handleSubmit = useCallback(async () => {
        if (!tableRef.current) return;

        const measurements = tableRef.current.getValues();

        if (measurements.length < 2) {
            setError('Enter at least 2 measurements before running the study.');
            return;
        }
        if (!formValues.tolerance || isNaN(Number(formValues.tolerance)) || Number(formValues.tolerance) <= 0) {
            setError('Enter a positive tolerance value before running the study.');
            return;
        }
        if (!formValues.referenceConfidence) {
            setError('Select a reference classification before running the study.');
            return;
        }
        if (!formValues.description.trim()) {
            setError('Enter a study description.');
            return;
        }

        setError(null);
        setSubmitting(true);

        const payload = {
            studyType:           'type1',
            description:          formValues.description.trim(),
            tolerance:            Number(formValues.tolerance),
            referenceValue:       formValues.referenceConfidence === 'not_established' || formValues.referenceValue === ''
                ? null
                : Number(formValues.referenceValue),
            k:                    Number(formValues.k) || 0.2,
            referenceConfidence:  formValues.referenceConfidence,
            measurements,
        };

        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const res = await apiFetch(`${backendUrl}/api/msa`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body:    JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(apiErrorMessage(res.status, data, 'Study failed. Check inputs and retry.'));
            } else {
                setStudyResults({
                    id:          data.id,
                    createdAt:   data.createdAt,
                    description: data.description,
                    results:     data.results,
                });
            }
        } catch (e) {
            setError('Network error. Check your connection and retry.');
        } finally {
            setSubmitting(false);
        }
    }, [formValues]);

    const handleClear = () => {
        setStudyResults(null);
        setError(null);
    };

    // Patch study feedback into the results JSONB via a PUT/PATCH if the endpoint exists.
    // For now we store it locally; the backend can extend later.
    const handleSaveFeedback = useCallback(({ studyId, conclusion, notes }) => {
        setStudyResults(prev => prev
            ? { ...prev, results: { ...prev.results, feedback: { conclusion, notes } } }
            : prev
        );
    }, []);

    const hasResults = Boolean(studyResults?.results);

    return (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* ------------------------------------------------------------------ */}
            {/* Left Drawer                                                         */}
            {/* ------------------------------------------------------------------ */}
            <Drawer
                variant="persistent"
                anchor="left"
                open={drawerOpen}
                sx={{
                    width: drawerOpen ? DRAWER_WIDTH : 0,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        backgroundColor: 'rgba(30,36,44,0.98)',
                        borderRight: '1px solid rgba(144,202,249,0.15)',
                        position: 'relative',
                        height: '100%',
                        overflowX: 'hidden',
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1.5, gap: 1.5, overflowY: 'auto' }}>
                    {/* Drawer header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pb: 1, borderBottom: '1px solid rgba(144,202,249,0.12)', flexShrink: 0 }}>
                        <IconButton
                            size="small"
                            onClick={onBack}
                            sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5, mr: 0.5, '&:hover': { color: '#90caf9' } }}
                            title="Back to study selector"
                        >
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, mr: 'auto', fontSize: '0.8rem' }}>
                            Type 1 GRR — Bias &amp; Repeatability
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            disabled={!hasResults}
                            title="Clear results"
                            sx={{ color: hasResults ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)', p: 0.5, '&:hover': { color: '#ff8a80' } }}
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={12} color="inherit" /> : <SendIcon />}
                            sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem', ml: 0.5 }}
                        >
                            {submitting ? 'Running…' : 'Run Study'}
                        </Button>
                        <IconButton
                            onClick={() => setDrawerOpen(false)}
                            size="small"
                            sx={{ color: 'rgba(144,202,249,0.6)', p: 0.5, ml: 0.25 }}
                            title="Collapse panel"
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Error */}
                    {error && (
                        <Typography variant="caption" sx={{ color: '#e57373', px: 0.5, lineHeight: 1.4 }}>
                            {error}
                        </Typography>
                    )}

                    {/* Form */}
                    <Type1GRRForm values={formValues} onChange={setFormValues} />

                    {/* Data entry table */}
                    <Box sx={{ flex: 1, minHeight: 200, display: 'flex', flexDirection: 'column' }}>
                        <Type1DataEntryTable
                            ref={tableRef}
                            description={formValues.description}
                            tolerance={formValues.tolerance}
                            referenceValue={formValues.referenceValue}
                            k={formValues.k}
                        />
                    </Box>
                </Box>
            </Drawer>

            {/* ------------------------------------------------------------------ */}
            {/* Main content area                                                   */}
            {/* ------------------------------------------------------------------ */}
            <Box
                component="main"
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: { xs: 2, md: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {/* Floating re-open button when drawer is collapsed */}
                {!drawerOpen && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDrawerOpen(true)}
                        startIcon={<ScienceIcon />}
                        sx={{
                            alignSelf: 'flex-start',
                            borderColor: 'rgba(144,202,249,0.4)',
                            color: '#90caf9',
                            '&:hover': { borderColor: '#90caf9', backgroundColor: 'rgba(144,202,249,0.06)' },
                        }}
                    >
                        Open Data Entry
                    </Button>
                )}

                {/* Results */}
                {hasResults && (
                    <Type1GRRResults
                        results={studyResults.results}
                        description={studyResults.description}
                        createdAt={studyResults.createdAt}
                        studyId={studyResults.id}
                        onSaveFeedback={handleSaveFeedback}
                    />
                )}

                {/* Empty state */}
                {!hasResults && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, opacity: 0.5, pt: 8 }}>
                        <ScienceIcon sx={{ fontSize: 56, color: 'rgba(144,202,249,0.4)' }} />
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', maxWidth: 360 }}>
                            Fill in the study parameters, enter measurements, and click <strong>Run Study</strong> to see results.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default Type1GRRPage;
